import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { SearchResultItem } from '@/lib/gs-api/resources-api';

@customElement('search-results-element')
export class SearchResultsElement extends LitElement {
	@property({ type: String })
	locale = 'en';

	@property({ type: String })
	appUrl!: string;

	@property({ type: Object })
	searchResults: {
		data: undefined | Array<SearchResultItem>;
		error: undefined | Error;
	} = {
		data: undefined,
		error: undefined,
	};

	// To allow Bootstrap css
	createRenderRoot() {
		return this;
	}

	errorTemplate() {
		return html` <p class="text-center my-3">
			Something went wrong. Please try again later.
		</p>`;
	}

	browseResourcesTemplate(type?: 'link') {
		// TODO: Replace with <gs-button> link variant when available
		if (type && type === 'link') {
			return html`<click-tracker
				data-event-name="Click Browse Resources"
				data-track-source="search_modal"
			>
				<a
					href=${'/' + this.locale + '/tools-and-resources'}
					class="btn btn-link"
				>
					Browse Resources
				</a>
			</click-tracker>`;
		}

		return html`<click-tracker
			data-event-name="Click Browse Resources"
			data-track-source="search_modal"
		>
			<gs-button href=${'/' + this.locale + '/tools-and-resources'}>
				Browse Resources
			</gs-button>
		</click-tracker>`;
	}

	noResultsTemplate() {
		return html` <div class="d-flex flex-column align-items-center gap-3 py-5">
			<img
				src="/_astro/images/empty-state-drawing.png"
				alt=""
				width="80"
				height="80"
			/>
			<p class="text-center mb-0">No resources found.</p>
			${this.browseResourcesTemplate()}
		</div>`;
	}

	resultsTemplate() {
		return html`<ul class="list-unstyled mb-0">
				${this.searchResults.data?.map((item) => {
					return html` <li class="results-card-item px-5 py-3">
						<click-tracker
							data-event-name="Access Resource"
							data-track-source="search_modal"
							data-track-name=${item.title}
							data-track-url=${item.url
								? item.url
								: `${this.appUrl}/resources/${item.id}`}
						>
							<a
								href=${item.url
									? item.url
									: `${this.appUrl}/resources/${item.id}`}
								target="_blank"
								rel="noopener noreferrer"
								class="results-card-link text-decoration-none"
							>
								<div
									class="d-flex flex-column flex-md-row gap-3 align-items-start"
								>
									<img
										src=${item.imageUrl}
										alt=${item.title}
										class="results-card-img object-fit-cover img-fluid rounded flex-shrink-0"
										aria-hidden="true"
									/>
									<div>
										<h2 class="h6 font-weight-bold mb-1">${item.title}</h2>
										<p class="overflow-hidden text-ellipsis mb-0">
											${item.description}
										</p>
									</div>
								</div>
							</a>
						</click-tracker>
					</li>`;
				})}
			</ul>
			<div class="text-center my-3">
				${this.browseResourcesTemplate('link')}
			</div>`;
	}

	render() {
		if (this.searchResults && this.searchResults.data) {
			if (this.searchResults.data.length > 0) {
				return this.resultsTemplate();
			}

			return this.noResultsTemplate();
		}

		if (this.searchResults && this.searchResults.error) {
			console.error('Search Results Error:', this.searchResults.error);
		}

		return this.errorTemplate();
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'search-results-element': SearchResultsElement;
	}
}
